import {
  ActionButtons,
  Badge,
  DataTable,
  EmptyState,
  type DataTableColumn,
  type DataTableExpandedTableConfig,
  type DataTableRow,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import { BALANCE_BADGE_COLOR } from '@constants/Billing'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import {
  BalanceBadgeStatus,
  BillingProcedure,
  InvoiceEntry,
  LedgerEntry,
  PaymentStatus,
} from '@dataTypes/billing.types'
import {
  formatCurrency,
  formatInvoiceNumber,
  hasBillingListNarrowed,
} from '@utils/billing.utils'
import React, { useCallback, useMemo, useState } from 'react'
import PrintInvoiceModal from './PrintInvoiceModal'
import useInvoice from './useInvoice'
import './InvoiceTableStyle.css'

interface Props {
  searchQuery: string
  filters: Record<string, string[]>
  onAddInvoice?: (invoice: InvoiceEntry) => void
  onCreateInvoice?: () => void
  suppressTableLoading?: boolean
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const DEFAULT_SORT_COLUMN_ID = 'invoiceDate'
const DEFAULT_SORT_DIRECTION: DataTableSortDirection = 'desc'

const LEDGER_ENTRY_COLUMNS: DataTableColumn[] = [
  { id: 'appointmentType', label: 'Appt. Type' },
  { id: 'appointmentDate', label: 'Appt. date' },
  { id: 'providerName', label: 'Provider name' },
  { id: 'clinicFee', label: 'Charges' },
  { id: 'approvedInsurance', label: 'Approved ins.' },
  { id: 'discount', label: 'Discount' },
  { id: 'appointmentAdjustment', label: 'Appointment adj.' },
  { id: 'patientResponsibility', label: 'Patient resp.' },
  { id: 'statusSpacer', label: '', renderCell: () => '' },
]

const PROCEDURE_COLUMNS: DataTableColumn[] = [
  { id: 'procedureCode', label: 'Procedure code' },
  { id: 'description', label: 'Description', colSpan: 2 },
  { id: 'clinicFee', label: 'Clinic fee' },
  { id: 'approvedInsurance', label: 'Approved ins.' },
  { id: 'discount', label: 'Discount' },
  { id: 'appointmentAdjustment', label: 'Appointment adj.' },
  { id: 'patientResponsibility', label: 'Patient resp.' },
  { id: 'statusSpacer', label: '', renderCell: () => '' },
]

const resolveBalanceStatus = (
  balance: number,
  cashAndAdjustment: number,
): BalanceBadgeStatus => {
  if (cashAndAdjustment > 0 && balance !== 0) {
    return BalanceBadgeStatus.Partial
  }
  if (balance === 0 && cashAndAdjustment > 0) {
    return BalanceBadgeStatus.Paid
  }
  return BalanceBadgeStatus.Unpaid
}

const sumPatientCashAndAdjustment = (invoice: InvoiceEntry): number =>
  (invoice.ledgerEntries || []).reduce(
    (ledgerSum, ledgerEntry) =>
      ledgerSum +
      (ledgerEntry.procedures || []).reduce(
        (procedureSum, procedure) =>
          procedureSum +
          (procedure.patientCash || 0) +
          (procedure.patientAdjustment || 0),
        0,
      ),
    0,
  )

const invoiceStatusColor = (
  status: string,
): 'vivid-green' | 'red' | 'yellow' | 'gray' => {
  if (status === PaymentStatus.Paid) return 'vivid-green'
  if (status === PaymentStatus.Unpaid) return 'red'
  if (status === PaymentStatus.PartiallyPaid) return 'yellow'
  return 'gray'
}

const toLedgerRow = (entry: LedgerEntry): DataTableRow => ({
  id: entry.appointmentId,
  appointmentType: entry.appointmentType || '-',
  appointmentDate: entry.appointmentDate || '-',
  providerName: entry.providerName || '-',
  clinicFee: formatCurrency(entry.clinicFee),
  approvedInsurance: formatCurrency(entry.approvedInsurance),
  discount: formatCurrency(entry.discount),
  appointmentAdjustment: formatCurrency(entry.appointmentAdjustment),
  patientResponsibility: formatCurrency(entry.patientResponsibility),
})

const toProcedureRow = (procedure: BillingProcedure): DataTableRow => ({
  id: procedure.procedureLogId,
  procedureCode: procedure.procedureCode || '-',
  description: procedure.description || '-',
  clinicFee: formatCurrency(procedure.clinicFee),
  approvedInsurance: formatCurrency(procedure.approvedInsurance),
  discount: formatCurrency(procedure.discount),
  appointmentAdjustment: formatCurrency(
    (procedure.patientAdjustment || 0) + (procedure.insuranceAdjustment || 0),
  ),
  patientResponsibility: formatCurrency(procedure.patientResponsibility),
})

const InvoiceTable: React.FC<Props> = ({
  searchQuery,
  filters,
  onAddInvoice,
  onCreateInvoice,
  suppressTableLoading = false,
}) => {
  const hooks = useInvoice(searchQuery, filters, { suppressTableLoading })
  const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceEntry | null>(
    null,
  )
  const [invoiceToPrint, setInvoiceToPrint] = useState<InvoiceEntry | null>(
    null,
  )
  const [sortColumnId, setSortColumnId] = useState(DEFAULT_SORT_COLUMN_ID)
  const [sortDirection, setSortDirection] = useState<DataTableSortDirection>(
    DEFAULT_SORT_DIRECTION,
  )
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([])

  const invoicesById = useMemo(() => {
    const invoices = new Map<string, InvoiceEntry>()
    hooks.invoices.forEach((invoice) => {
      invoices.set(invoice.invoiceNo, invoice)
    })
    return invoices
  }, [hooks.invoices])

  const ledgerById = useMemo(() => {
    const entries = new Map<string, LedgerEntry>()
    hooks.invoices.forEach((invoice) => {
      ;(invoice.ledgerEntries || []).forEach((entry) => {
        entries.set(entry.appointmentId, entry)
      })
    })
    return entries
  }, [hooks.invoices])

  const rows = useMemo(
    (): DataTableRow[] =>
      hooks.invoices.map((invoice) => ({
        id: invoice.invoiceNo,
        invoiceNo: formatInvoiceNumber(invoice.invoiceNo),
        invoiceDate: invoice.invoiceDate,
        patientName: invoice.patientName,
        charges: formatCurrency(invoice.charges),
        approvedInsurance: formatCurrency(invoice.approvedInsurance),
        discount: formatCurrency(invoice.discount),
        appointmentAdjustment: formatCurrency(invoice.appointmentAdjustment),
      })),
    [hooks.invoices],
  )

  const handleSort = useCallback(
    (columnId: string): void => {
      const nextDirection: DataTableSortDirection =
        sortColumnId === columnId && sortDirection === 'asc' ? 'desc' : 'asc'
      setSortColumnId(columnId)
      setSortDirection(nextDirection)
      hooks.handleTableSortChange({
        key: columnId,
        direction:
          nextDirection === 'asc' ? SortDirection.ASC : SortDirection.DESC,
      })
    },
    [hooks, sortColumnId, sortDirection],
  )

  const columns = useMemo((): DataTableColumn[] => {
    return [
      {
        id: 'invoiceNo',
        label: 'Invoice no.',
        sortable: false,
        width: '123px',
      },
      {
        id: 'invoiceDate',
        label: 'Invoice date',
        sortable: true,
        width: '80px',
      },
      {
        id: 'patientName',
        label: 'Patient name',
        sortable: true,
        width: '160px',
      },
      {
        id: 'charges',
        label: 'Charges',
        sortable: false,
        width: '84px',
      },
      {
        id: 'approvedInsurance',
        label: 'Approved ins.',
        sortable: false,
        width: '84px',
      },
      {
        id: 'discount',
        label: 'Discount',
        sortable: false,
        width: '75px',
      },
      {
        id: 'appointmentAdjustment',
        label: 'Appointment adj.',
        sortable: false,
        width: '96px',
      },
      {
        id: 'patientResponsibility',
        label: 'Patient resp.',
        sortable: false,
        width: '117px',
        renderCell: (row) => {
          const invoice = invoicesById.get(row.id)
          if (!invoice) return null
          const patientBalance = invoice.patientResponsibilityBalance || 0
          const patientStatus = resolveBalanceStatus(
            patientBalance,
            sumPatientCashAndAdjustment(invoice),
          )
          return (
            <span>
              {formatCurrency(invoice.patientResponsibilityTotal)}{' '}
              <Badge
                mode="tinted"
                color={BALANCE_BADGE_COLOR[patientStatus]}
                label={formatCurrency(patientBalance)}
              />
            </span>
          )
        },
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        width: '86px',
        renderCell: (row) => {
          const invoice = invoicesById.get(row.id)
          if (!invoice) return null
          return (
            <Badge
              mode="tinted"
              color={invoiceStatusColor(invoice.status)}
              label={invoice.status}
            />
          )
        },
      },
    ]
  }, [invoicesById])

  const expandedTable = useMemo(
    (): DataTableExpandedTableConfig => ({
      columns: LEDGER_ENTRY_COLUMNS,
      expandable: true,
      expandableColumnId: 'appointmentType',
      getRows: (parentRow) => {
        const invoice = invoicesById.get(parentRow.id)
        if (!invoice) return []
        return (invoice.ledgerEntries || []).map(toLedgerRow)
      },
      expandedTable: {
        columns: PROCEDURE_COLUMNS,
        getRows: (ledgerRow) => {
          const entry = ledgerById.get(ledgerRow.id)
          if (!entry) return []
          return (entry.procedures || []).map(toProcedureRow)
        },
      },
    }),
    [invoicesById, ledgerById],
  )

  const renderActions = (row: DataTableRow): React.ReactNode => {
    const invoice = invoicesById.get(row.id)
    if (!invoice) return null

    const canDelete =
      invoice.patientResponsibilityBalance ===
      invoice.patientResponsibilityTotal

    return (
      <ActionButtons
        onPayment={() => onAddInvoice?.(invoice)}
        disablePayment={invoice.patientResponsibilityBalance === 0}
        onPrint={() => setInvoiceToPrint(invoice)}
        onDelete={() => setInvoiceToDelete(invoice)}
        disableDelete={!canDelete}
      />
    )
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!invoiceToDelete) return
    const invoiceNo = invoiceToDelete.invoiceNo
    setInvoiceToDelete(null)
    await hooks.confirmDeleteInvoice(invoiceNo)
  }

  const listNarrowed = hasBillingListNarrowed(searchQuery, filters)
  const showEmptyState = !hooks.loadingInvoices && hooks.invoices.length === 0
  const hasNoRecords = !listNarrowed
  const totalPages = Math.max(1, Math.ceil(hooks.invoiceCount / hooks.pageSize))

  if (showEmptyState) {
    return (
      <div className="billing-tab-panel billing-tab-panel--empty invoice-table">
        <EmptyState
          iconClass="mgc mgc_bill_2_fill"
          title="No invoices found"
          description={
            hasNoRecords
              ? "You don't have any invoices on record yet."
              : 'No invoices match your search.'
          }
          actionLabel={hasNoRecords ? 'Create Invoice' : undefined}
          onAction={hasNoRecords ? onCreateInvoice : undefined}
        />
      </div>
    )
  }

  return (
    <>
      <DataTable
        className="invoice-table"
        columns={columns}
        rows={rows}
        expandable
        expandableColumnId="invoiceNo"
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={setExpandedRowIds}
        expandedTable={expandedTable}
        showActions
        actionsLabel="Actions"
        renderActions={renderActions}
        sortColumnId={sortColumnId}
        sortDirection={sortDirection}
        onSort={handleSort}
        pagination={{
          currentPage: hooks.currentPage,
          totalPages,
          pageSize: hooks.pageSize,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageChange: hooks.handlePageChange,
          onPageSizeChange: hooks.handlePageSizeChange,
        }}
      />
      <DeleteConfirmationModal
        show={!!invoiceToDelete}
        onHide={() => setInvoiceToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete invoice?"
        bodyText={`Are you sure you want to delete invoice ${invoiceToDelete ? formatInvoiceNumber(invoiceToDelete.invoiceNo) : ''}? This action can't be undone.`}
      />
      <PrintInvoiceModal
        show={!!invoiceToPrint}
        onHide={() => setInvoiceToPrint(null)}
        invoice={invoiceToPrint}
      />
    </>
  )
}

export default InvoiceTable
